import { Head } from '@inertiajs/react'
import { useMemo, useState } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import { Tabs, TabsContent } from '@/components/ui/Tabs'
import PlanHeader from '../components/PlanHeader'
import { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import ReviewPlanChangesTab from './components/ReviewPlanChangesTab'
import ReviewSubmitPopover from './components/ReviewSubmitPopover'
import ReviewTaskCard from './components/ReviewTaskCard'
import CommentCard, { type CommentProps } from './components/CommentCard'
import CommentComposer from './components/CommentComposer'
import SubmissionCard, { type SubmissionProps } from './components/SubmissionCard'
import RevisionBanner from './components/RevisionBanner'
import { changeCount, DiffLegend, type RevisionBaseline } from './components/DiffBits'
import { useReviewTimeline } from './components/useReviewTimeline'
import { type CellLink } from './components/reviewComments'
import ReviewTabsList from './components/ReviewTabsList'
import { type EditorRequest, type RequestComment } from './components/revisionRequest'
import { type Person, type Task } from '@/pages/Tasks/task'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import { plan_index_path, plan_top_down_review_comment_path, tasks_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import { monthRangeLabel, quarterRangeLabel, revisionLabels } from './components/periodLabel'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
  // Present when the submitted draft revises an already-published plan.
  revision_diff: RevisionBaseline | null
  // Present when the task under review is a manager's revision request.
  revision_request: EditorRequest | null
  review_task: Task
  people: Person[]
  submission: SubmissionProps
  comments: CommentProps[]
  cell_comments: RequestComment[]
  read_only: boolean
}

// The reviewer's page, GitHub PR layout: the submitted draft rendered
// read-only (same sections as the recap), the submission message as a
// timeline comment, the task as a sidebar. The verdict lives in the app
// header's Submit review popover (ReviewSubmitPopover), not on the card.
// A revision adds a banner with how much the target moves (topped by a
// one-line how-to for the reviewer) and, in the Plan changes tab,
// GitHub-style green/red diff marks (see DiffBits).
export default function Review({
  begin_date,
  end_date,
  top_down_plan,
  period,
  plan_period,
  actuals_months,
  plan_nodes,
  channel_section,
  quarterly_section,
  revision_diff,
  revision_request,
  review_task,
  people,
  submission,
  comments,
  cell_comments,
  read_only,
}: Props) {
  const market = useMarket()
  const [tab, setTab] = useState('conversation')
  // The cell a Conversation comment linked to; `at` makes a second click on
  // the same link scroll again.
  const [focus, setFocus] = useState<(CellLink & { at: number }) | null>(null)
  const openCell = (cell: CellLink) => {
    setFocus({ ...cell, at: Date.now() })
    setTab('changes')
  }
  const timeline = useReviewTimeline(comments, cell_comments)
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // " for Q4 2026" on a quarterly revision, named by its first quarter like
  // the task title; nothing on a full-plan revision.
  const revisedFor = actuals_months != null
    ? ` for ${quarterRangeLabel(plan_period.start, plan_period.start)} ${plan_period.start.slice(0, 4)}`
    : ''
  // Full year = actual sales (top-level nodes only, to avoid double-counting
  // descendants) + this version's plan target.
  const fullYearTarget = useMemo(
    () => plan_nodes.reduce((sum, n) => sum + Number(n.actualRevenue ?? 0), 0) + top_down_plan.revenue_target,
    [plan_nodes, top_down_plan.revenue_target]
  )

  return (
    <AppLayout
      header={<PlanHeader
        name={top_down_plan.name}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        beginDate={begin_date}
        endDate={end_date}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
      headerActions={submission.can_review && (
        <ReviewSubmitPopover
          submissionId={submission.id}
          planName={top_down_plan.name}
          revision={revision_diff != null || actuals_months != null}
          approveBackTo={plan_index_path(market.slug)}
          sendBackBackTo={tasks_path()}
        />
      )}
    >
      <Head title={`${top_down_plan.name} — Review`} />
      {revision_diff && (
        <RevisionBanner
          baseline={revision_diff}
          planNodes={plan_nodes}
          currency={top_down_plan.currency}
          legend=""
          instruction={submission.can_review && !revision_diff.first_publish && (
            <>
              {submission.submitted_by} revised the plan{revisedFor}.<br />
              Go through the Conversation and Plan changes below, then approve or send it
              back with <span className="font-semibold">Submit review</span>.
            </>
          )}
          className="mb-4"
        />
      )}
      {read_only && (
        <div className="mb-4 rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground">
          Approved{submission.resolved_by ? ` by ${submission.resolved_by}` : ''}
          {submission.resolved_at ? ` on ${new Date(submission.resolved_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
        </div>
      )}
      {revision_request && (
        <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
          <span className="font-semibold">{revision_request.requested_by ?? 'Someone'}</span> asked for these changes
          <span className="text-muted-foreground"> · {revision_request.comments.length} {revision_request.comments.length === 1 ? 'comment' : 'comments'} in the Plan changes tab.</span>
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <ReviewTabsList
          commentCount={timeline.length}
          changeCount={revision_diff ? changeCount(revision_diff) : null}
          aside={tab === 'changes' && revision_diff && !revision_diff.first_publish && revision_diff.summary.any_changes && (
            <DiffLegend currency={top_down_plan.currency}>
              <span><span className="font-semibold">Past</span> = {monthRangeLabel(period.start, period.end)} (for comparison)</span>
            </DiffLegend>
          )}
        />
        <TabsContent value="conversation">
          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-col gap-4 lg:w-3/5">
              <SubmissionCard submission={submission} />
              {timeline.map((comment) => (
                <CommentCard key={comment.id} comment={comment} submissionId={submission.id} readOnly={read_only}
                  reviewCommentPath={(id) => plan_top_down_review_comment_path(market.slug, top_down_plan.slug, review_task.id, id)}
                  onOpenCell={openCell} />
              ))}
              {!read_only && <CommentComposer submissionId={submission.id} />}
            </div>
            <ReviewTaskCard task={review_task} people={people} />
          </div>
        </TabsContent>
        <TabsContent value="changes">
          <ReviewPlanChangesTab
            slug={top_down_plan.slug}
            taskId={review_task.id}
            planNodes={plan_nodes}
            channelSection={channel_section}
            quarterlySection={quarterly_section}
            currency={top_down_plan.currency}
            periodLabels={periodLabels}
            baseline={revision_diff}
            revisionRequest={revision_request}
            cellComments={cell_comments}
            focus={focus}
            readOnly={read_only}
          />
        </TabsContent>
      </Tabs>
    </AppLayout>
  )
}
